import * as sphere from 'ol/sphere'

export function areaToString(area, units, labels, options) {
  labels = labels || {}
  if (area === undefined || area === null) return '-'

  options = options || {}
  const showUnit = options.onlyValue === undefined || !options.onlyValue

  if (units !== 'imperial') {
    if (options.alwaysFieldUnits || area >= 1000) {
      return (
        (area / 10000).toFixed(2) + (showUnit ? ' ' + (labels.ha || 'ha') : '')
      )
    } else if (area < 0.1) {
      return (
        (area * 10000).toFixed(0) +
        (showUnit ? ' ' + (labels.cm2 || 'cm²') : '')
      )
    } else {
      return (
        area.toFixed(area < 10 ? 2 : area < 100 ? 1 : 0) +
        (showUnit ? ' ' + (labels.m2 || 'm²') : '')
      )
    }
  } else {
    if (options.alwaysFieldUnits || area >= 404.6856) {
      // 0.1 acres
      return (area * 0.000247105).toFixed(2) + (showUnit ? ' ac' : '')
    } else if (area < 1.93548) {
      return (area * 1550.0031).toFixed(0) + (showUnit ? ' in²' : '')
    } else {
      return (area * 10.7639).toFixed(0) + (showUnit ? ' ft²' : '')
    }
  }
}

export function distanceToString(length, units, labels, options) {
  if (length === undefined || length === null) return '-'
  options = options || {}
  const decimals =
    options.decimals == null || typeof options.decimals !== 'number'
      ? 2
      : options.decimals
  const absLength = Math.abs(length)

  if (units !== 'imperial') {
    if (absLength > 1000) {
      return (
        (length / 1000).toFixed(decimals) +
        (options.onlyValue ? '' : ' ' + ((labels && labels.km) || 'km'))
      )
    } else if (absLength < 1) {
      return (
        (length * 100).toFixed(Math.max(0, decimals - 2)) +
        (options.onlyValue ? '' : ' cm')
      )
    } else {
      return (
        length.toFixed(decimals) +
        (options.onlyValue ? '' : ' ' + ((labels && labels.m) || 'm'))
      )
    }
  } else {
    if (absLength > 1609) {
      return (
        (length / 1609.34).toFixed(decimals) + (options.onlyValue ? '' : ' mi')
      )
    } else {
      return (
        (length / 0.3048).toFixed(decimals) + (options.onlyValue ? '' : ' ft')
      )
    }
  }
}

export function resolutionToString(r, units) {
  if (r === undefined || r === null) return '-'

  const resolutionInUnits = units !== 'imperial' ? r : r * 0.393701
  return `${resolutionInUnits.toFixed(getNumberDecimals(resolutionInUnits))} ${
    units !== 'imperial' ? 'cm' : 'in'
  }/px`
}

export function massToString(m, units, labels, options) {
  if (m === undefined || m === null) return '-'

  options = options || {}
  const showUnit = options.onlyValue === undefined || !options.onlyValue
  return units !== 'imperial'
    ? m.toFixed(1) + (showUnit ? ' ' + 'kg' : '')
    : (m * 2.20462).toFixed(1) + (showUnit ? ' lb' : '')
}

export function prescriptionToString(p, units, labels, options) {
  if (p === undefined || p === null) return '-'

  options = options || {}
  const showUnit = options.onlyValue === undefined || !options.onlyValue
  return units !== 'imperial'
    ? p.toFixed(1) + (showUnit ? ' kg/ha' : '')
    : (p * 0.892179).toFixed(1) + (showUnit ? ' lb/ac' : '')
}

export function density(x, area, units) {
  return units !== 'imperial'
    ? x / (area / 10000) // units / ha
    : x / (area * 0.000247105) // units / ac
}

export function densityToString(x, area, units, name) {
  var d = density(x, area, units)
  return (
    d.toFixed(0) + ' ' + name + '/' + (units !== 'imperial' ? 'ha' : 'acre')
  )
}

export function areaInMeters(geom) {
  return Math.abs(sphere.getArea(geom, { projection: 'EPSG:4326' }))
}

export function geometryAreaToString(geom, units, labels) {
  return areaToString(areaInMeters(geom), units, labels)
}

export function bytesSizeToString(bytes) {
  if (isNaN(bytes)) return 'Unknown'

  var sizes = [
    [1024 * 1024 * 1024, 'GB', 1],
    [1024 * 1024, 'MB', 1],
    [1024, 'KB', 1],
    [1, 'bytes', 0],
  ]

  bytes = Number(bytes)

  var i
  for (i = 0; i < sizes.length - 1 && bytes < sizes[i][0]; i++);
  return `${(bytes / sizes[i][0]).toFixed(sizes[i][2])} ${sizes[i][1]}`
}

export function toHumanSize(bytes) {
  if (isNaN(bytes)) return 'Unknown'

  const sizes = [
    [1024 * 1024 * 1024, 'GB', 1],
    [1024 * 1024, 'MB', 1],
    [1024, 'KB', 1],
    [1, 'bytes', 0],
  ]

  bytes = Number(bytes)

  let i
  for (i = 0; i < sizes.length - 1 && bytes < sizes[i][0]; i++);
  return `${(bytes / sizes[i][0]).toFixed(sizes[i][2])} ${sizes[i][1]}`
}

export function gramsToTon(grams) {
  if (grams === undefined || grams === null) return '-'
  return `${(grams / (1000 * 1000)).toFixed(1)} tons`
}

const numberFormat = new Intl.NumberFormat()

export function formatNumber(n) {
  return numberFormat.format(n)
}

/**
 * Given a delta x where x is the length of a range (min,max), returns a number of decimals that is
 * suitable for display of values within this range. For small ranges, a larger number of decimals is
 * chosen, for large ranges fewer decimals are used.
 *
 * @param {number} x the length of the range
 * @param {number} fudge a fudge factor where a higher value will skew the return value towards more decimals (default is 1.2)
 */
export function getNumberDecimals(x, fudge = 1.2) {
  return Math.ceil(Math.max(0, -Math.log10(Math.abs(x)) + fudge))
}
